<script setup lang="ts">
import { computed } from 'vue'
import { CheckCheck, CloudUpload, FolderPlus, LibraryBig, TextCursorInput, Trash2, X } from '@lucide/vue'
import { Button, ResponsiveActionMenu } from 'catalyst-vue'
import { useLibraryEntriesStore } from '../../stores/library-entries'
import { selectAllKeyLabel } from '../../lib/library-shortcuts'
import { useControlSize, useLibraryLayout } from '../../lib/library-layout'

/** backupCount 是選取中「這台裝置備份得了」的本數，由書庫頁算出來後傳進來。 */
const props = defineProps<{ backupCount: number }>()
const emit = defineEmits<{ categories: []; collection: []; edit: []; remove: []; upload: [] }>()
const store = useLibraryEntriesStore()
const compact = useLibraryLayout()
const controlSize = useControlSize()

const total = computed(() => store.selectedCount)
const inFilter = computed(() => store.selectedInFilter)
// 兩個數字相同時只顯示一個；不同時都要呈現，否則「篩選後全選、清掉篩選、再刪除」會超出預期。
const countLabel = computed(() => total.value === inFilter.value
  ? `已選取 ${total.value} 本`
  : `已選取 ${total.value} 本（目前篩選結果內 ${inFilter.value} 本）`)

// 細指標環境才顯示按鍵：觸控裝置沒有鍵盤，那段字只是噪音。兩個動作在任何環境都可點。
const keyHint = (label: string, key: string) => compact.value ? label : `${label}（${key}）`
const actions = computed(() => [
  { key: 'select-all', label: keyHint('全選目前結果', selectAllKeyLabel()), icon: CheckCheck },
  { key: 'clear', label: '清除選取', icon: X, disabled: !total.value },
  { key: 'categories', label: '加入或移除分類', icon: FolderPlus, disabled: !total.value },
  { key: 'collection', label: '加入集合', icon: LibraryBig, disabled: !total.value },
  { key: 'edit', label: '批次編輯標題或作者', icon: TextCursorInput, disabled: !total.value },
  // 備份只做得到選取中有本機檔案又還沒備份的那些，因此標籤直接帶上適用本數。
  { key: 'upload', label: `加入備份佇列（${props.backupCount} 本）`, icon: CloudUpload, disabled: !props.backupCount },
  { key: 'remove', label: '刪除選取的作品', icon: Trash2, tone: 'error' as const, disabled: !total.value },
])

function select(key: string) {
  if (key === 'select-all') { store.selectAllFiltered(); return }
  if (key === 'clear') { store.clearSelected(); return }
  if (key === 'categories') { emit('categories'); return }
  if (key === 'collection') { emit('collection'); return }
  if (key === 'edit') { emit('edit'); return }
  if (key === 'upload') { emit('upload'); return }
  if (key === 'remove') emit('remove')
}
</script>

<template>
  <!-- 常駐區塊而非浮動列：浮動列會蓋住清單最後幾筆，而列表檢視的重點正是一屏看到很多筆。 -->
  <div class="selection-action-bar" role="group" aria-label="多選動作">
    <span class="selection-count" role="status" aria-live="polite">{{ countLabel }}</span>
    <div class="selection-actions">
      <!-- ResponsiveActionMenu 沒有 size prop，size 會透過 attrs 落到觸發用的按鈕上，
           因此與旁邊的「離開多選」共用同一個尺寸來源，不會一高一矮。 -->
      <ResponsiveActionMenu
        :mode="compact ? 'sheet' : 'dropdown'"
        :size="controlSize"
        button-label="多選動作"
        title="多選動作"
        sheet-description="套用到已選取的作品。"
        :actions="actions"
        @select="select"
      />
      <Button plain :size="controlSize" :title="compact ? undefined : '離開多選（Esc）'" @click="store.exitSelectionMode()">離開多選</Button>
    </div>
  </div>
</template>
